import { Routes } from '@angular/router';

import { AccessDeniedComponent } from '@core/components/access-denied/access-denied.component';
import { ApiDownComponent } from '@core/components/api-down/api-down.component';
import { NoDataspaceComponent } from '@core/components/no-dataspace/no-dataspace.component';
import { NotFoundComponent } from '@core/components/not-found/not-found.component';
import { RootLayoutComponent } from '@core/components/root-layout/root-layout.component';
import { UnderConstructionComponent } from '@core/components/under-construction/under-construction.component';
import { adminGuard } from '@core/guards/admin.guard';
import { authGuard } from '@core/guards/auth.guard';
import { dataspaceRedirectGuard } from '@core/guards/dataspace-redirect.guard';
import { dataspaceGuard } from '@core/guards/dataspace.guard';
import { guestGuard } from '@core/guards/guest.guard';
import { optimizationsGuard } from '@core/guards/optimizations.guard';
import { planEditLeaveGuard } from '@core/guards/plan-edit-leave.guard';

import { SEGMENT_CRUMB } from '@shared/breadcrumb/breadcrumb.model';

import { AppShellComponent } from '@features/components/app-shell/app-shell.component';
import { AssistantWindowComponent } from '@features/components/assistant-window/assistant-window.component';
import { EntitiesListComponent } from '@features/components/entities/entities-list.component';
import { EntityEditPageComponent } from '@features/components/entities/entity-edit-page/entity-edit-page.component';
import { EntityComponent } from '@features/components/entities/entity/entity.component';
import { JobHelpedImportationComponent } from '@features/components/entities/job-helped-importation/job-helped-importation.component';
import { TransformerUploadComponent } from '@features/components/entities/transformer-upload/transformer-upload.component';
import { HomeComponent } from '@features/components/home/home.component';
import { LoginComponent } from '@features/components/login/login.component';
import { PlanDeployComponent } from '@features/components/plan/deploy/plan-deploy.component';
import { PlanComponent } from '@features/components/plan/plan.component';
import { PlanningSpaceListComponent } from '@features/components/planning-space-list/planning-space-list.component';
import { PlanningSpaceComponent } from '@features/components/planning-space/planning-space.component';
import { SolverCfgComponent } from '@features/components/solver-cfg/solver-cfg.component';
import { SolverComponent } from '@features/components/solver/solver.component';
import { SolversListComponent } from '@features/components/solvers-list/solvers-list.component';
import { DataVisualizationPopupComponent } from '@features/components/visualization/data-visualization-popup/data-visualization-popup.component';
import { termsPageRoutes } from '@features/terms-page/terms-page.routes';

import { AppRoutes } from './app.paths';
import {
  ADMIN_CRUMB,
  CUSTOM_GEN_CRUMB,
  DATA_CRUMB,
  DEPLOY_CRUMB,
  EDIT_CRUMB,
  ENTITY_TYPE_CRUMB,
  HOME_CRUMB,
  NEW_CRUMB,
  OPTIMIZATIONS_CRUMB,
  PLANNING_SPACES_CRUMB,
  PLANNING_SPACE_NAME_CRUMB,
  PLAN_DATA_CRUMB,
  PLAN_NAME_CRUMB,
  PROFILE_CRUMB,
  REPORTING_CRUMB,
  SOLVERS_CRUMB,
  SOLVER_NAME_CRUMB,
  SOLVER_PARAM_NAME_CRUMB,
  UPLOAD_CRUMB
} from './breadcrumb.crumbs';

// Shared route definition for entity operations
// This is reused in both global data context and plan data context
const DATA_ROUTES: Routes = [
  { path: '', component: EntitiesListComponent },
  {
    path: ':entity',
    data: { breadcrumb: ENTITY_TYPE_CRUMB },
    children: [
      { path: '', component: EntityComponent },
      { path: 'new', redirectTo: 'new/form', pathMatch: 'full' },
      {
        path: 'new/:mode',
        component: EntityEditPageComponent,
        data: { breadcrumb: [NEW_CRUMB, null] }
      },
      { path: 'edit/:entityUid', redirectTo: 'edit/:entityUid/form', pathMatch: 'full' },
      {
        path: 'edit/:entityUid/:mode',
        component: EntityEditPageComponent,
        data: { breadcrumb: [EDIT_CRUMB, SEGMENT_CRUMB, null] }
      },
      {
        path: 'upload',
        component: TransformerUploadComponent,
        data: { breadcrumb: UPLOAD_CRUMB }
      },
      {
        path: 'custom-gen/:genName',
        component: JobHelpedImportationComponent,
        data: { breadcrumb: [CUSTOM_GEN_CRUMB, SEGMENT_CRUMB] }
      }
    ]
  }
];

/**
 * Where angular-auth-oidc-client navigates when Keycloak ends a login with `access_denied`
 * (e.g. the user declined the terms of use). Its default, `/unauthorized`, is not a route here.
 */
export const OIDC_UNAUTHORIZED_ROUTE = `/${AppRoutes.accessDenied}`;

export const routes: Routes = [
  // Non-authenticated routes
  { path: AppRoutes.login, component: LoginComponent, canActivate: [guestGuard] },
  {
    path: AppRoutes.dataVisualizationPopup.root,
    component: DataVisualizationPopupComponent
  },
  {
    path: `${AppRoutes.assistantWindow.root}/ds/:${AppRoutes.dataspace.params.dataspaceId}`,
    component: AssistantWindowComponent,
    canActivate: [authGuard, dataspaceGuard]
  },
  { path: 'no-dataspace', component: NoDataspaceComponent },
  { path: 'api-down', component: ApiDownComponent },
  { path: AppRoutes.accessDenied, component: AccessDeniedComponent },
  ...termsPageRoutes,

  // Authenticated routes — all nested under RootLayoutComponent
  {
    path: '',
    component: RootLayoutComponent,
    canActivate: [authGuard],
    children: [
      // Redirect guard for root path
      {
        path: '',
        canActivate: [dataspaceRedirectGuard],
        children: []
      },
      // Admin section (lazy-loaded)
      {
        path: 'admin',
        canActivate: [adminGuard],
        data: { breadcrumb: ADMIN_CRUMB },
        loadChildren: () =>
          import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES)
      },
      // Profile section (lazy-loaded)
      {
        path: 'profile',
        data: { breadcrumb: PROFILE_CRUMB },
        loadChildren: () =>
          import('./features/profile/profile.routes').then((m) => m.PROFILE_ROUTES)
      },
      // Lists jobs across every data space the user can reach, so it sits outside any of them.
      {
        path: AppRoutes.optimizations.root,
        canActivate: [optimizationsGuard],
        data: { breadcrumb: OPTIMIZATIONS_CRUMB },
        loadComponent: () =>
          import('@features/optimization-history/optimization-history.component').then(
            (m) => m.OptimizationHistoryComponent
          )
      },
      // Dataspace section
      {
        path: AppRoutes.dataspace.root,
        canActivate: [dataspaceGuard],
        children: [
          // Main dataspace shell
          {
            path: AppRoutes.home,
            component: AppShellComponent,
            children: [
              {
                path: '',
                component: HomeComponent,
                data: { breadcrumb: HOME_CRUMB }
              },
              {
                path: AppRoutes.reporting.root,
                component: UnderConstructionComponent,
                data: { breadcrumb: REPORTING_CRUMB }
              },
              {
                path: AppRoutes.data.root,
                data: { breadcrumb: DATA_CRUMB },
                children: DATA_ROUTES
              },
              {
                path: AppRoutes.solvers.root,
                data: { breadcrumb: SOLVERS_CRUMB },
                children: [
                  {
                    path: '',
                    component: SolversListComponent
                  },
                  {
                    path: AppRoutes.solvers.view.path,
                    data: { breadcrumb: SOLVER_NAME_CRUMB },
                    children: [
                      {
                        path: '',
                        children: [
                          {
                            path: '',
                            component: SolverComponent
                          },
                          {
                            path: 'config/:paramId',
                            component: SolverCfgComponent,
                            data: { breadcrumb: SOLVER_PARAM_NAME_CRUMB }
                          }
                        ]
                      }
                    ]
                  }
                ]
              },
              {
                path: AppRoutes.plan.root,
                data: { breadcrumb: PLANNING_SPACES_CRUMB },
                children: [
                  {
                    path: '',
                    component: PlanningSpaceListComponent
                  },
                  {
                    path: AppRoutes.plan.plans.path,
                    data: { breadcrumb: PLANNING_SPACE_NAME_CRUMB },
                    children: [
                      {
                        path: '',
                        component: PlanningSpaceComponent
                      },
                      {
                        path: AppRoutes.plan.plans.view.path,
                        canDeactivate: [planEditLeaveGuard],
                        data: { breadcrumb: PLAN_NAME_CRUMB },
                        children: [
                          {
                            path: '',
                            component: PlanComponent
                          },
                          // Deploy page lives under plan/:planId so plan context
                          // (RouteStateSyncService) is preserved. MUST come before the
                          // legacy ':entity' redirect, otherwise 'deploy' is captured as
                          // an entity name and redirected to data/deploy.
                          {
                            path: AppRoutes.plan.plans.view.deploy.path,
                            component: PlanDeployComponent,
                            data: { breadcrumb: DEPLOY_CRUMB }
                          },
                          // DEPRECATED: Redirect legacy entity routes to new structure
                          {
                            path: ':entity',
                            redirectTo: 'data/:entity',
                            pathMatch: 'full'
                          },
                          // Plan data routes (reuses DATA_ROUTES for consistency)
                          {
                            path: AppRoutes.planData.root,
                            data: { breadcrumb: PLAN_DATA_CRUMB },
                            children: DATA_ROUTES
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },

  { path: '**', component: NotFoundComponent }
];
